"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import PhotoSwipeLightbox from "photoswipe/lightbox";
import "photoswipe/style.css";
import {
  adminCommonFileDelete,
  adminCommonFileDownload,
  adminCommonFileImageRotate,
  adminCommonFileUploader,
} from "@/entities";
import { getFileUrl } from "@/shared";

interface Props {
  queryKey: string;
  table: string;
  column: string;
  id: any;
  timestamp: string;
  file: any;
  accept: string;
}
export function FileEditUploader({
  queryKey,
  table,
  column,
  id,
  timestamp,
  file,
  accept,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: adminCommonFileUploader,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success(`이미지가 업로드되었습니다.`);
    },
    onError: (e: any) => {
      toast.error(`에러(${e.message})가 발생했습니다. 다시 시도해 주세요.`);
    },
  });
  const onUploadFile = async (e: any) => {
    e.preventDefault();

    try {
      if (updateMutation.isPending) return;

      const files = e.target.files;
      const file = files[0];

      await updateMutation.mutateAsync({
        file,
        timestamp,
        table,
        column,
        id,
      });
    } catch (e) {
      console.log(e);
      toast.error("에러가 발생했습니다. 다시 시도해 주세요.");
    }
  };
  const onClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const deleteMutation = useMutation({
    mutationFn: adminCommonFileDelete,
    onSuccess: () => {
      toast.success(`이미지가 삭제되었습니다.`);
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
    onError: (e: any) => {
      toast.error(`에러(${e.message})가 발생했습니다. 다시 시도해 주세요.`);
    },
  });
  const onDeleteFile = async () => {
    try {
      if (!window.confirm("정말 삭제하시겠습니까?")) return;
      if (deleteMutation.isPending) return;

      await deleteMutation.mutateAsync({ file });
    } catch (e) {
      console.log(e);
    }
  };

  const [isDownLoading, setIsDownLoading] = useState<boolean>(false);
  const downloadMutation = useMutation({
    mutationFn: adminCommonFileDownload,
    onSuccess: () => {
      toast.success(`이미지가 다운로드되었습니다.`);
    },
    onError: (e: any) => {
      toast.error(`에러(${e.message})가 발생했습니다. 다시 시도해 주세요.`);
    },
  });
  const onDownloadFile = useCallback(async () => {
    try {
      setIsDownLoading(true);
      if (downloadMutation.isPending) return;

      const { result } = await downloadMutation.mutateAsync({
        common_file_id: file?.id,
      });

      const blobUrl = window.URL.createObjectURL(result as Blob);
      const link = document.createElement("a");

      link.setAttribute("href", blobUrl);
      link.setAttribute("download", "cover.png");
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.log(e);
    } finally {
      setIsDownLoading(false);
    }
  }, []);

  const [isRotating, setIsRotating] = useState<boolean>(false);
  const rotateMutation = useMutation({
    mutationFn: adminCommonFileImageRotate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success(`이미지가 회전되었습니다.`);
    },
    onError: (e: any) => {
      toast.error(`에러(${e.message})가 발생했습니다. 다시 시도해 주세요.`);
    },
  });
  const onRetateFile = useCallback(async (id: any, angle: string) => {
    try {
      setIsRotating(true);
      if (rotateMutation.isPending) return;

      await rotateMutation.mutateAsync({ id, angle });
    } catch (e) {
      console.log(e);
    } finally {
      setIsRotating(false);
    }
  }, []);

  useEffect(() => {
    const lightbox = new PhotoSwipeLightbox({
      gallery: `#${column}`,
      children: "a",
      pswpModule: () => import("photoswipe"),
    });
    lightbox.init();

    return () => lightbox.destroy();
  }, []);

  return (
    <>
      {file?.file_url ? (
        <>
          <div className="coverUpload-img bg-gray-100" id={column}>
            <div className="preview w-200px">
              <div
                className="img"
                style={{
                  backgroundImage: file?.type.startsWith("image/")
                    ? `url("${getFileUrl(file?.file_url)}")`
                    : `url("/assets/images/default_product.png")`,
                }}
              />
              <div className="preview_button">
                <div className="flex">
                  <div className="w-20 text-ellipsis overflow-hidden text-center fw-bold text-white">
                    {file?.name}
                  </div>
                  <div className="flex flex-row py-1">
                    <button
                      className="btn btn-white btn-xs basis-1/2 mx-1"
                      type="button"
                      onClick={() => onDeleteFile()}
                    >
                      <i className="fa fa-trash"></i>
                      {/* 삭제 */}
                    </button>

                    <button
                      className="btn btn-white btn-xs basis-1/2 mx-1"
                      type="button"
                      disabled={isDownLoading}
                      onClick={() => onDownloadFile()}
                    >
                      <i className="fa fa-download"></i>
                      {/* 다운 */}
                    </button>

                    <a
                      href={getFileUrl(file?.file_url)}
                      data-pswp-width={1000}
                      data-pswp-height={1000}
                      className="btn btn-white btn-xs basis-1/2 mx-1 preview"
                    >
                      <i className="fa fa-magnifying-glass"></i>
                      {/* 확대 */}
                    </a>
                  </div>
                  {file?.type.startsWith("image/") && (
                    <div className="flex flex-row py-1">
                      <button
                        className="btn btn-white btn-xs basis-1/2 mx-1"
                        type="button"
                        disabled={isRotating}
                        onClick={() => onRetateFile(file.id, "-90")}
                      >
                        <i className="fa fa-rotate-left"></i>
                        {/* 회전 */}
                      </button>
                      <button
                        className="btn btn-white btn-xs basis-1/2 mx-1"
                        type="button"
                        disabled={isRotating}
                        onClick={() => onRetateFile(file.id, "90")}
                      >
                        <i className="fa fa-rotate-right"></i>
                        {/* 회전 */}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="coverUpload-img bg-gray-100">
            <div className="coverUpload-list">
              <input
                ref={fileInputRef}
                type="file"
                id="coverImage"
                onChange={onUploadFile}
                className="hide"
                accept={accept ?? undefined}
              />
              <button type="button" className="upload-btn" onClick={onClick}>
                <i className="fa fa-upload fa-lg"></i>
                <br />
                업로드
              </button>
            </div>
          </div>
        </>
      )}
      <div className="coverUpload-box">
        <div className="coverUpload-info">
          <p className="mb-0 fs-10px">파일 : .PNG .JPG</p>
          <p className="mb-0 fs-10px">
            최대크기 : <b>1,000px x 1,000px</b>
          </p>
        </div>
      </div>
    </>
  );
}
